package fr.frux.feature.auth

import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import fr.frux.core.common.config.SiteLinks
import fr.frux.feature.auth.resources.Res
import fr.frux.feature.auth.resources.auth_code_not_received
import fr.frux.feature.auth.resources.auth_legal_notice
import fr.frux.feature.auth.resources.auth_legal_privacy
import fr.frux.feature.auth.resources.auth_legal_terms
import org.jetbrains.compose.resources.stringResource

/** Un lien à poser dans une phrase déjà formatée : le libellé tel qu'il y apparaît, et sa cible. */
internal data class InlineLink(
    val label: String,
    val url: String,
)

/**
 * Pose chaque [links] sur la première occurrence de son libellé dans [text].
 *
 * Le libellé est cherché dans la phrase formatée plutôt que découpé autour des `%s` : l'ordre des
 * arguments peut changer d'une langue à l'autre sans toucher au code. Un libellé vide ou absent
 * reste du texte simple, ce qui arrive au premier rendu web où `stringResource` rend `""`.
 */
internal fun annotateLinks(
    text: String,
    links: List<InlineLink>,
    linkStyles: TextLinkStyles? = null,
): AnnotatedString =
    buildAnnotatedString {
        append(text)
        links.forEach { link ->
            if (link.label.isEmpty()) return@forEach
            val start = text.indexOf(link.label)
            if (start < 0) return@forEach
            addLink(LinkAnnotation.Url(link.url, linkStyles), start, start + link.label.length)
        }
    }

/** Style des liens posés dans une phrase de l'écran de connexion, partagé pour qu'ils se ressemblent. */
@Composable
private fun authLinkStyles(): TextLinkStyles =
    TextLinkStyles(
        style =
            SpanStyle(
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Medium,
                textDecoration = TextDecoration.Underline,
            ),
    )

/**
 * Information au point de collecte de l'email (#468, RGPD art. 13) : acceptation des conditions
 * et lien vers la politique de confidentialité. Les liens passent par `LinkAnnotation.Url`, que
 * Compose ouvre avec le `LocalUriHandler` et expose au lecteur d'écran comme des liens.
 */
@Composable
internal fun LegalNotice(modifier: Modifier = Modifier) {
    val terms = stringResource(Res.string.auth_legal_terms)
    val privacy = stringResource(Res.string.auth_legal_privacy)
    val sentence = stringResource(Res.string.auth_legal_notice, terms, privacy)
    val linkStyles = authLinkStyles()
    Text(
        text =
            annotateLinks(
                text = sentence,
                links =
                    listOf(
                        InlineLink(terms, SiteLinks.TERMS),
                        InlineLink(privacy, SiteLinks.PRIVACY),
                    ),
                linkStyles = linkStyles,
            ),
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = TextAlign.Center,
        modifier = modifier,
    )
}

/**
 * « Code non reçu ? » sous le champ du code (#484) : ouvre la section du centre d'aide qui liste
 * les causes (spam, adresse mal saisie, délai). Tout le libellé est le lien, exposé comme tel au
 * lecteur d'écran par `LinkAnnotation.Url`.
 */
@Composable
internal fun CodeNotReceivedLink(modifier: Modifier = Modifier) {
    val label = stringResource(Res.string.auth_code_not_received)
    Text(
        text =
            annotateLinks(
                text = label,
                links = listOf(InlineLink(label, SiteLinks.HELP_CODE_NOT_RECEIVED)),
                linkStyles = authLinkStyles(),
            ),
        style = MaterialTheme.typography.bodyMedium,
        modifier = modifier,
    )
}
